import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import PageBanner from "@/components/PageBanner";
import Reveal from "@/components/Reveal";
import { PRODUCTS } from "@/data/site";

export default function Products() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="products-page">
      <PageBanner
        eyebrow="The Collection"
        title="Apple cider vinegar & wellness essentials"
        subtitle="Every bottle is crafted in small batches from organically grown apples. Browse the range and tap any product to learn more."
        testId="products-banner"
      />

      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08}>
              <button
                onClick={() => setActive(p)}
                data-testid={`product-card-${p.id}`}
                data-cursor="hover"
                className="group block w-full text-left"
              >
                <div className="relative overflow-hidden rounded-3xl border border-line bg-sand">
                  <span className="absolute left-4 top-4 z-10 rounded-full bg-cream/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-forest backdrop-blur">
                    {p.tag}
                  </span>
                  <div className="aspect-[4/5] w-full overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading={i > 2 ? "lazy" : "eager"}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-xl text-forest">{p.name}</h3>
                    <p className="mt-1 text-sm text-ink/60">{p.subtitle}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-lg text-forest">{p.price}</p>
                    <p className="text-xs text-ink/50">{p.size}</p>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[80] flex items-end justify-center bg-forest/40 p-0 backdrop-blur-sm md:items-center md:p-6"
            data-testid="product-modal"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative grid max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-3xl bg-cream md:grid-cols-2 md:rounded-3xl"
            >
              <button
                onClick={() => setActive(null)}
                data-testid="product-modal-close"
                aria-label="Close"
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-cream/90 text-forest transition-colors hover:bg-sand"
              >
                <X className="h-5 w-5" strokeWidth={1.6} />
              </button>
              <div className="aspect-square w-full overflow-hidden md:aspect-auto">
                <img src={active.image} alt={active.name} className="h-full w-full object-cover" />
              </div>
              <div className="p-7 md:p-9">
                <span className="rounded-full bg-sand px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-sage">
                  {active.tag}
                </span>
                <h3 className="mt-4 font-heading text-3xl text-forest">{active.name}</h3>
                <p className="mt-1 text-sm text-ink/60">{active.subtitle}</p>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="font-heading text-2xl text-forest">{active.price}</span>
                  <span className="text-sm text-ink/50">/ {active.size}</span>
                </div>
                <p className="mt-5 text-sm leading-relaxed text-ink/70">{active.description}</p>
                <ul className="mt-6 space-y-2.5">
                  {active.highlights.map((h) => (
                    <li key={h} className="flex items-center gap-3 text-sm text-ink/75">
                      <Check className="h-4 w-4 shrink-0 text-sage" strokeWidth={2} />
                      {h}
                    </li>
                  ))}
                </ul>
                <a
                  href="/contact"
                  data-testid="product-modal-enquire"
                  className="mt-8 inline-block rounded-full bg-sage px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest"
                >
                  Enquire to order
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
